'use client';

import React, { useState } from 'react';
import { useZuper } from './ZuperContext';
import { ZUPER_COMPANY_NAME } from './types';
import { useTheme } from '@/lib/ThemeContext';
import { IconSun, IconMoon } from '@tabler/icons-react';
import styles from '@/styles/Zuper.module.css';

export function LoginPage() {
  const { login, isLoading, error } = useZuper();
  const { theme, toggleTheme } = useTheme();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);

    if (!email.trim() || !password.trim()) {
      setLocalError('Please enter both email and password');
      return;
    }

    const success = await login({ email: email.trim(), password });
    if (!success && !error) {
      setLocalError('Login failed. Please try again.');
    }
  };

  const displayError = localError || error;

  return (
    <div className={styles.loginContainer}>
      <button
        className={styles.themeToggle}
        onClick={toggleTheme}
        title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
      >
        {theme === 'dark' ? <IconSun size={20} /> : <IconMoon size={20} />}
      </button>

      <div className={styles.loginCard}>
        {/* Collaboration Logos */}
        <div className={styles.collabLogos}>
          <div className={styles.collabLogoWrapper}>
            <img src="/zuper-logo.png" alt="Zuper" />
          </div>
          <div className={styles.collabLogoWrapper}>
            <img src="/ericsson-logo.png" alt="Ericsson" />
          </div>
          <div className={styles.collabLogoWrapper}>
            <img src="/vonage-logo.png" alt="Vonage" />
          </div>
        </div>

        <h1 className={styles.loginTitle}>Remote Assist</h1>
        <p className={styles.loginSubtitle}>Sign in to continue</p>

        <div className={styles.companyBadge}>
          <span className={styles.companyLabel}>Company</span>
          <span className={styles.companyName}>{ZUPER_COMPANY_NAME}</span>
        </div>

        {displayError && (
          <div className={styles.errorBanner}>
            {displayError}
          </div>
        )}

        <form onSubmit={handleSubmit} className={styles.loginForm}>
          <div className={styles.formGroup}>
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              className={styles.input}
              disabled={isLoading}
              autoComplete="email"
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="password">Password</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              className={styles.input}
              disabled={isLoading}
              autoComplete="current-password"
            />
          </div>

          <button
            type="submit"
            className={styles.loginButton}
            disabled={isLoading}
          >
            {isLoading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
      </div>
    </div>
  );
}
